import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  ArrowLeft,
  Plus,
  Home as HomeIcon,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Loader2,
  Upload,
  X,
  Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminImmobili = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [managingImagesProperty, setManagingImagesProperty] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    zone: '',
    monthly_rent: '',
    property_type: '',
    sqm: '',
    rooms: '',
    bathrooms: '',
    floor: '',
    furnished: false
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchProperties();
  }, [navigate]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${BACKEND_URL}/api/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data.properties || []);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Compress and process images
      for (const file of files) {
        try {
          // Compress image
          const compressedBlob = await compressImage(file);
          const compressedFile = new File([compressedBlob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });
          
          setSelectedImages(prev => [...prev, compressedFile]);
          
          // Create preview
          const reader = new FileReader();
          reader.onloadend = () => {
            setImagePreviews(prev => [...prev, reader.result]);
          };
          reader.readAsDataURL(compressedFile);
        } catch (error) {
          console.error('Error processing image:', error);
          alert(`Errore nel processare ${file.name}`);
        }
      }
    }
  };

  // Image compression function
  const compressImage = (file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Canvas to Blob failed'));
              }
            },
            file.type,
            quality
          );
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('adminToken');
      const submitData = new FormData();
      
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });
      
      selectedImages.forEach((image) => {
        submitData.append('images', image);
      });

      if (editingProperty) {
        // Update existing property (without images)
        await axios.put(
          `${BACKEND_URL}/api/admin/properties/${editingProperty.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new property
        await axios.post(
          `${BACKEND_URL}/api/admin/properties`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      setShowCreateModal(false);
      setEditingProperty(null);
      resetForm();
      fetchProperties();
    } catch (err) {
      alert('Errore nel salvataggio');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddImages = async () => {
    if (!managingImagesProperty || selectedImages.length === 0) return;

    setUploadingImages(true);
    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      
      selectedImages.forEach((image) => {
        formData.append('images', image);
      });

      await axios.post(
        `${BACKEND_URL}/api/admin/properties/${managingImagesProperty.id}/images`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      alert('Foto aggiunte con successo!');
      setShowImageModal(false);
      setManagingImagesProperty(null);
      resetForm();
      fetchProperties();
    } catch (err) {
      alert('Errore nel caricamento delle foto');
      console.error(err);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (propertyId, imageId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa foto?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${BACKEND_URL}/api/admin/properties/${propertyId}/images/${imageId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      const updatedProperty = managingImagesProperty.images.filter(img => img.id !== imageId);
      setManagingImagesProperty({
        ...managingImagesProperty,
        images: updatedProperty
      });
      
      fetchProperties();
    } catch (err) {
      alert('Errore nell\'eliminazione della foto');
      console.error(err);
    }
  };

  const togglePublish = async (property) => {
    try {
      const token = localStorage.getItem('adminToken');
      const newStatus = property.status === 'published' ? 'draft' : 'published';
      
      await axios.put(
        `${BACKEND_URL}/api/admin/properties/${property.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchProperties();
    } catch (err) {
      alert('Errore nell\'aggiornamento dello stato');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo immobile?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${BACKEND_URL}/api/admin/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchProperties();
    } catch (err) {
      alert('Errore nell\'eliminazione');
      console.error(err);
    }
  };

  const openEditModal = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      description: property.description,
      zone: property.zone,
      monthly_rent: property.monthly_rent,
      property_type: property.property_type,
      sqm: property.sqm || '',
      rooms: property.rooms || '',
      bathrooms: property.bathrooms || '',
      floor: property.floor || '',
      furnished: property.furnished
    });
    setShowCreateModal(true);
  };

  const openImageModal = (property) => {
    setManagingImagesProperty(property);
    setShowImageModal(true);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      zone: '',
      monthly_rent: '',
      property_type: '',
      sqm: '',
      rooms: '',
      bathrooms: '',
      floor: '',
      furnished: false
    });
    setSelectedImages([]);
    setImagePreviews([]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="ghost">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Gestione Immobili</h1>
                <p className="text-slate-600">Crea e gestisci gli annunci</p>
              </div>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setEditingProperty(null);
                setShowCreateModal(true);
              }}
              className="bg-gradient-to-r from-emerald-500 to-emerald-600"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nuovo Immobile
            </Button>
          </div>
        </div>
      </div>

      {/* Properties List */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {properties.length === 0 ? (
          <Card className="p-12 text-center">
            <HomeIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              Nessun immobile ancora
            </h3>
            <p className="text-slate-600 mb-6">
              Crea il primo annuncio per la vetrina
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-5 h-5 mr-2" />
              Crea Immobile
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <Card key={property.id} className="overflow-hidden">
                <div className="relative h-48 bg-slate-100">
                  {property.images && property.images.length > 0 ? (
                    <img
                      src={`${BACKEND_URL}/api/property-image/${property.id}/${property.images[0].id}`}
                      alt={property.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '';
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <HomeIcon className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    {property.status === 'published' ? (
                      <span className="bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        Pubblicato
                      </span>
                    ) : (
                      <span className="bg-slate-500 text-white text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1">
                        <EyeOff className="w-3 h-3" />
                        Bozza
                      </span>
                    )}
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="bg-black/50 text-white text-xs px-2 py-1 rounded">
                      {property.images?.length || 0} foto
                    </span>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-lg font-bold text-slate-900 mb-1">
                    {property.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-2">{property.zone}</p>
                  <p className="text-xl font-bold text-emerald-600 mb-4">
                    €{property.monthly_rent}/mese
                  </p>

                  <div className="flex gap-2 mb-2">
                    <Button
                      onClick={() => togglePublish(property)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      {property.status === 'published' ? (
                        <>
                          <EyeOff className="w-4 h-4 mr-1" />
                          Nascondi
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          Pubblica
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => openEditModal(property)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(property.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <Button
                    onClick={() => openImageModal(property)}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Gestisci Foto
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6 overflow-y-auto">
          <Card className="w-full max-w-3xl bg-white p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">
                {editingProperty ? 'Modifica Immobile' : 'Nuovo Immobile'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingProperty(null);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Titolo *</Label>
                  <Input
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="es: Monolocale luminoso zona centro"
                  />
                </div>

                <div>
                  <Label>Zona *</Label>
                  <Input
                    required
                    value={formData.zone}
                    onChange={(e) => setFormData({...formData, zone: e.target.value})}
                    placeholder="es: Centro, Zona Stazione"
                  />
                </div>

                <div>
                  <Label>Canone Mensile (€) *</Label>
                  <Input
                    type="number"
                    required
                    value={formData.monthly_rent}
                    onChange={(e) => setFormData({...formData, monthly_rent: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Tipo Immobile *</Label>
                  <Input
                    required
                    value={formData.property_type}
                    onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                    placeholder="es: Monolocale, Bilocale"
                  />
                </div>

                <div>
                  <Label>Metri Quadri</Label>
                  <Input
                    type="number"
                    value={formData.sqm}
                    onChange={(e) => setFormData({...formData, sqm: e.target.value})}
                  />
                </div>

                <div>
                  <Label>N. Stanze</Label>
                  <Input
                    type="number"
                    value={formData.rooms}
                    onChange={(e) => setFormData({...formData, rooms: e.target.value})}
                  />
                </div>

                <div>
                  <Label>N. Bagni</Label>
                  <Input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({...formData, bathrooms: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Piano</Label>
                  <Input
                    value={formData.floor}
                    onChange={(e) => setFormData({...formData, floor: e.target.value})}
                    placeholder="es: 2, Terra, Attico"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.furnished}
                    onCheckedChange={(checked) => setFormData({...formData, furnished: checked})}
                  />
                  <Label>Arredato</Label>
                </div>
              </div>

              <div>
                <Label>Descrizione *</Label>
                <Textarea
                  required
                  rows={6}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Descrizione dettagliata dell'immobile..."
                />
              </div>

              {!editingProperty && (
                <div>
                  <Label>Foto</Label>
                  <p className="text-xs text-slate-500 mb-2">Le foto vengono automaticamente ottimizzate per il caricamento</p>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-emerald-500 transition-colors">
                    <label htmlFor="images" className="cursor-pointer block">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Upload className="w-8 h-8 text-slate-400" />
                        <p className="text-slate-600">Click per caricare foto</p>
                        <p className="text-xs text-slate-400">JPG, PNG, WEBP (max 10MB per foto)</p>
                      </div>
                      <input
                        id="images"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {imagePreviews.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-4">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={preview}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-24 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {editingProperty && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    💡 Per modificare le foto, usa il pulsante "Gestisci Foto" nella card dell'immobile
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingProperty(null);
                    resetForm();
                  }}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Salvataggio...
                    </>
                  ) : editingProperty ? (
                    'Salva Modifiche'
                  ) : (
                    'Crea Immobile'
                  )}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Image Management Modal */}
      {showImageModal && managingImagesProperty && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6 overflow-y-auto">
          <Card className="w-full max-w-4xl bg-white p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold">Gestisci Foto</h2>
                <p className="text-slate-600">{managingImagesProperty.title}</p>
              </div>
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setManagingImagesProperty(null);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Existing Images */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3">Foto Attuali ({managingImagesProperty.images?.length || 0})</h3>
              {managingImagesProperty.images && managingImagesProperty.images.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {managingImagesProperty.images.map((image, index) => (
                    <div key={image.id} className="relative group">
                      <img
                        src={`${BACKEND_URL}/api/property-image/${managingImagesProperty.id}/${image.id}`}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        onClick={() => handleDeleteImage(managingImagesProperty.id, image.id)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600">Nessuna foto caricata</p>
                </div>
              )}
            </div>

            {/* Add New Images */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Aggiungi Nuove Foto</h3>
              <p className="text-xs text-slate-500 mb-3">💡 Le foto vengono ottimizzate automaticamente (max 1920x1080, quality 80%)</p>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 hover:border-emerald-500 transition-colors">
                <label htmlFor="new-images" className="cursor-pointer block">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <Upload className="w-8 h-8 text-slate-400" />
                    <p className="text-slate-600">Click per caricare nuove foto</p>
                    <p className="text-xs text-slate-400">Supporta foto multiple</p>
                  </div>
                  <input
                    id="new-images"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>

              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-24 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowImageModal(false);
                  setManagingImagesProperty(null);
                  resetForm();
                }}
                className="flex-1"
              >
                Chiudi
              </Button>
              <Button
                onClick={handleAddImages}
                disabled={uploadingImages || selectedImages.length === 0}
                className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600"
              >
                {uploadingImages ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Caricamento...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-5 w-5" />
                    Aggiungi Foto ({selectedImages.length})
                  </>
                )}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminImmobili;
